import { mediaLoadEvent } from "./videoKind";

// Global, cross-component video telemetry.
//
// Every <video> / <media-player> instance on the site — product cards,
// showcase gallery, hero, collections, modals, ... — reports its lifecycle
// through this single module instead of logging on its own, so:
//   - every "created" / "loaded" / "playing" / "paused" line carries a live
//     page-wide concurrency count (no more guessing how many decoders are
//     actually active from scattered per-component logs)
//   - window.mediaStats() gives an on-demand snapshot of every tracked
//     instance, grouped by context (product-card, showcase-gallery, hero, ...)
//
// DEV only (import.meta.env.DEV) — every export is a no-op in production, so
// call sites don't need their own guard.
//
// Usage: const id = mediaCreated({ context: "hero", kind: "background",
// title: "...", src }); ...player.addEventListener("can-play", () =>
// mediaLoaded(id)); on play -> mediaPlaying(id); on pause -> mediaPaused(id);
// when the instance is fully torn down (src cleared / element removed) ->
// mediaDestroyed(id).

const DEV = import.meta.env.DEV;

let seq = 0;
const registry = new Map(); // id -> meta { context, kind, title, src }
const loaded = new Set();
const playing = new Set();

function label(id) {
  const meta = registry.get(id);
  if (!meta) return id;
  return `${meta.context}/${meta.kind} · ${meta.title || "?"} · ${meta.src ?? ""}`;
}

function line(action, id) {
  console.log(
    `[media] ${action.padEnd(9)} ${label(id)} — playing ${playing.size} · loaded ${loaded.size} · created ${registry.size}`,
  );
}

export function mediaCreated(meta) {
  if (!DEV) return null;
  const id = `m${++seq}`;
  registry.set(id, meta);
  line("created", id);
  return id;
}

export function mediaLoaded(id) {
  if (!DEV || !id || loaded.has(id)) return;
  loaded.add(id);
  line("loaded", id);
}

export function mediaPlaying(id) {
  if (!DEV || !id || playing.has(id)) return;
  playing.add(id);
  line("playing", id);
}

export function mediaPaused(id) {
  if (!DEV || !id || !playing.has(id)) return;
  playing.delete(id);
  line("paused", id);
}

export function mediaDestroyed(id) {
  if (!DEV || !id) return;
  if (playing.has(id)) line("paused", id); // a destroyed instance can't stay "playing"
  playing.delete(id);
  loaded.delete(id);
  registry.delete(id);
}

// For markup-driven <media-player>/<video> elements that aren't pooled
// (hero, collection cards, category push, logo, loader, ...): attaches real
// DOM/Vidstack media events instead of requiring every play()/pause() call
// site to report manually — accurate regardless of what triggers playback
// (native autoplay, a play button, hover, JS .play()). Call once when the
// component finds/creates its element; returns a disposer that stops
// tracking (call it from the component's own teardown/destroy).
export function observeMediaElement(el, meta) {
  if (!DEV || !el) return () => {};

  const id = mediaCreated(meta);
  const loadEvent = mediaLoadEvent(el);

  const onLoaded = () => mediaLoaded(id);
  const onPlay = () => mediaPlaying(id);
  const onPause = () => mediaPaused(id);

  el.addEventListener(loadEvent, onLoaded);
  el.addEventListener("play", onPlay);
  el.addEventListener("pause", onPause);
  el.addEventListener("ended", onPause);

  return () => {
    el.removeEventListener(loadEvent, onLoaded);
    el.removeEventListener("play", onPlay);
    el.removeEventListener("pause", onPause);
    el.removeEventListener("ended", onPause);
    mediaDestroyed(id);
  };
}

if (DEV) {
  window.mediaStats = () => {
    const rows = [...registry.entries()].map(([id, meta]) => ({
      context: meta.context,
      kind: meta.kind,
      title: meta.title,
      src: meta.src,
      loaded: loaded.has(id),
      playing: playing.has(id),
    }));
    console.table(rows);
    const byContext = {};
    for (const { context, playing: p } of rows) {
      byContext[context] ??= { created: 0, playing: 0 };
      byContext[context].created++;
      if (p) byContext[context].playing++;
    }
    console.log(
      `[media] totals — created ${registry.size} · loaded ${loaded.size} · playing ${playing.size}`,
      byContext,
    );
    return { created: registry.size, loaded: loaded.size, playing: playing.size, byContext };
  };
}
